import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Users, Calendar, ShoppingCart, IndianRupee, 
  TrendingUp, Package, MapPin, ArrowUpRight, BarChart3 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#FF6B00', '#D4AF37', '#8B1A1A', '#4A6274', '#2D8B4E', '#9B59B6'];
const STATUS_COLORS = {
  pending: '#EAB308',
  confirmed: '#3B82F6',
  completed: '#22C55E',
  paid: '#22C55E',
  shipped: '#9B59B6',
  delivered: '#059669',
  cancelled: '#EF4444'
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/admin/analytics`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setAnalytics(analyticsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Revenue', value: `₹${stats?.total_revenue?.toLocaleString() || 0}`, icon: IndianRupee, color: 'from-emerald-500 to-emerald-600', change: '+12%' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, color: 'from-blue-500 to-blue-600', change: '+8%' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: ShoppingCart, color: 'from-purple-500 to-purple-600', change: '+15%' },
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'from-orange-500 to-orange-600', change: '+5%' },
    { label: 'Temples', value: stats?.total_temples || 0, icon: MapPin, color: 'from-teal-500 to-teal-600' },
    { label: 'Products', value: stats?.total_products || 0, icon: Package, color: 'from-pink-500 to-pink-600' },
  ];

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    paid: 'bg-green-100 text-green-700',
    shipped: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-lg border border-gray-100">
          <p className="font-semibold text-[#1A202C] text-sm">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ₹{entry.value?.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05, duration: 0.4 }}
            className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.06] shadow-[0_4px_20px_rgba(0,0,0,0.03)] card-hover relative overflow-hidden group"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            {/* Subtle background glow element */}
            <div className={`absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br ${stat.color} opacity-[0.08] rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500`}></div>
            
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-sm`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              {stat.change && (
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1.5 rounded-full flex items-center border border-emerald-100">
                  <TrendingUp className="w-3.5 h-3.5 mr-1" />
                  {stat.change}
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-gray-900 tracking-tight mb-1 relative z-10">{stat.value}</p>
            <p className="text-sm font-medium text-gray-500 relative z-10">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="lg:col-span-2 bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
          data-testid="revenue-chart"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900">Revenue Overview</h3>
              <p className="text-sm font-medium text-gray-500 mt-1">Monthly breakdown of bookings & orders</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100">
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={analytics?.monthly_revenue || []}>
              <defs>
                <linearGradient id="bookingGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF7B00" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#FF7B00" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="orderGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 13, fill: '#8f8f8f', fontWeight: 500 }} axisLine={false} tickLine={false} dy={10} />
              <YAxis tick={{ fontSize: 13, fill: '#8f8f8f', fontWeight: 500 }} axisLine={false} tickLine={false} dx={-10} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 13, fontWeight: 500, paddingTop: '20px' }} iconType="circle" />
              <Area type="monotone" dataKey="bookings" name="Puja Bookings" stroke="#FF7B00" fill="url(#bookingGrad)" strokeWidth={3} activeDot={{ r: 6, strokeWidth: 0, fill: '#FF7B00' }} />
              <Area type="monotone" dataKey="orders" name="Store Orders" stroke="#D4AF37" fill="url(#orderGrad)" strokeWidth={3} activeDot={{ r: 6, strokeWidth: 0, fill: '#D4AF37' }} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Booking Status Pie */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)] flex flex-col"
          data-testid="booking-status-chart"
        >
          <div className="mb-6">
            <h3 className="font-heading text-xl font-bold text-gray-900">Booking Status</h3>
            <p className="text-sm font-medium text-gray-500 mt-1">Distribution by current status</p>
          </div>
          {analytics?.booking_status?.length > 0 ? (
            <div className="flex-1 flex flex-col justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={analytics.booking_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={6}
                    dataKey="value"
                    stroke="none"
                  >
                    {analytics.booking_status.map((entry, index) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                {analytics.booking_status.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-2 text-sm font-medium px-3 py-1.5 bg-gray-50 rounded-lg">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[entry.name] || '#ccc' }} />
                    <span className="text-gray-700 capitalize">{entry.name} <span className="text-gray-400 ml-1">({entry.value})</span></span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm font-medium bg-gray-50 rounded-xl">No booking data yet</div>
          )}
        </motion.div>
      </div>

      {/* Temple Popularity + Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Temple Popularity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
          data-testid="temple-popularity-chart"
        >
          <div className="mb-6">
            <h3 className="font-heading text-xl font-bold text-gray-900">Temple Popularity</h3>
            <p className="text-sm font-medium text-gray-500 mt-1">Bookings by temple</p>
          </div>
          {analytics?.temple_popularity?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.temple_popularity} layout="vertical" barGap={8}>
                <CartesianGrid strokeDasharray="4 4" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 13, fill: '#8f8f8f', fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 13, fill: '#4a4a4a', fontWeight: 500 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{fill: 'rgba(255,123,0,0.05)'}} content={<CustomTooltip />} />
                <Bar dataKey="bookings" radius={[0, 8, 8, 0]} barSize={24}>
                  {analytics.temple_popularity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`url(#barGrad${index % 2})`} />
                  ))}
                </Bar>
                <defs>
                  <linearGradient id="barGrad0" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FF9E42" />
                    <stop offset="100%" stopColor="#FF7B00" />
                  </linearGradient>
                  <linearGradient id="barGrad1" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#E5C76B" />
                    <stop offset="100%" stopColor="#D4AF37" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-gray-400 text-sm font-medium bg-gray-50 rounded-xl">No booking data yet</div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
        >
          <div className="mb-6">
            <h3 className="font-heading text-xl font-bold text-gray-900">Quick Actions</h3>
            <p className="text-sm font-medium text-gray-500 mt-1">Fast track to administrative tools</p>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {[
              { label: 'View Bookings', path: '/admin/bookings', icon: Calendar, color: 'from-blue-500 to-indigo-600' },
              { label: 'View Orders', path: '/admin/orders', icon: ShoppingCart, color: 'from-purple-500 to-fuchsia-600' },
              { label: 'Manage Temples', path: '/admin/temples', icon: MapPin, color: 'from-teal-400 to-emerald-600' },
              { label: 'Manage Products', path: '/admin/products', icon: Package, color: 'from-rose-400 to-red-500' },
            ].map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className="flex flex-col p-6 bg-gray-50 border border-gray-100 rounded-2xl hover:bg-white hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center shadow-sm mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center justify-between w-full">
                  <span className="font-bold text-gray-900 text-base">{action.label}</span>
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#FF7B00]/10 transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#FF7B00] transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900">Recent Bookings</h3>
            </div>
            <Link to="/admin/bookings" className="text-[#FF7B00] text-sm font-bold hover:text-[#FF5100] transition-colors flex items-center gap-1">View All <ArrowUpRight className="w-4 h-4"/></Link>
          </div>
          <div className="space-y-4">
            {stats?.recent_bookings?.length > 0 ? (
              stats.recent_bookings.map((booking) => (
                <div key={booking.id} className="flex items-center justify-between p-4 bg-gray-50 border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-sm rounded-xl transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FF7B00]/10 flex items-center justify-center text-[#FF7B00] font-bold">
                      {booking.devotee_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{booking.devotee_name}</p>
                      <p className="text-xs font-medium text-gray-500 mt-0.5">{booking.sankalp_date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold ${statusColors[booking.status]}`}>
                      {booking.status}
                    </span>
                    <p className="text-base font-bold text-gray-900 mt-1.5">₹{booking.amount?.toLocaleString()}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center border border-dashed border-gray-200">
                <p className="text-gray-500 font-medium">No recent bookings</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="bg-white rounded-2xl p-8 border border-[#1A1A1A]/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.02)]"
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900">Recent Orders</h3>
            </div>
            <Link to="/admin/orders" className="text-[#FF7B00] text-sm font-bold hover:text-[#FF5100] transition-colors flex items-center gap-1">View All <ArrowUpRight className="w-4 h-4"/></Link>
          </div>
          <div className="space-y-4">
            {stats?.recent_orders?.length > 0 ? (
              stats.recent_orders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-sm rounded-xl transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                      <Package className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Order #{order.id?.slice(0, 8)}</p>
                      <p className="text-xs font-medium text-gray-500 mt-0.5">{order.items?.length || 0} items</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                    <p className="text-base font-bold text-gray-900 mt-1.5">₹{order.total_amount?.toLocaleString()}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center border border-dashed border-gray-200">
                <p className="text-gray-500 font-medium">No recent orders</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
